import { createSlice } from "@reduxjs/toolkit";
import {
  decrypt,
  encrypt,
  formatTime,
  getSpecificMedicationStatus,
  getActiveSlotForDate,
} from "../../utils";
import moment from "moment";
import { isLate } from "../../pages/Home/Medication/MarLogTab/MarLogTable";

const initialState = {
  selectedMarType: "",
  loading: false,
  addMarLoading: false,
  loadingMar_Types: false,

  medicationLogs: [],
  medicationLogCount: 0,
  medicationLogsAllData: [],

  mar: [],
  calendarEntries: [],
  marTypes: [],
  allmarTypes: [],
  marType: [],
  marAgainstTypes: [],
  filterDates: [undefined, undefined],
  logType: "all",
  logSearch: "",
  allData: [],
  newMarLogs: [],

  selectedClient: JSON.parse(localStorage.getItem("selectedClient")) || null,
  // selectedClientCopy must mirror selectedClient on init so that searchClient()
  // (dispatched by FilterBar on mount) doesn't wipe selectedClient.form3 before
  // a client is explicitly re-selected after a page reload.
  selectedClientCopy: JSON.parse(localStorage.getItem("selectedClient")) || null,
};

export const marState = createSlice({
  name: "mar",
  initialState,
  reducers: {
    setLoading(state, action) {
      return {
        ...state,
        loading: action?.payload,
      };
    },
    setLoadingMAR_Types(state, action) {
      return {
        ...state,
        loadingMAR_Types: action?.payload,
      };
    },
    setAddMarLoading(state, action) {
      return {
        ...state,
        addMarLoading: action?.payload,
      };
    },

    setCalendarEntries(state, action) {
      return {
        ...state,
        calendarEntries: action?.payload ?? [],
        loading: false,
      };
    },

    setMar(state, action) {
      let _mar = [];
      let _allData = [];
      // Use Redux state.selectedClient as the authoritative source — it is always
      // updated by setSelectedClient before getData() fires the API call that
      // eventually dispatches setMar. Reading localStorage here caused a race
      // condition where a stale client's form3 was used when two clients switched
      // rapidly or when getAllPatientsModal resolved asynchronously.
      const client = state.selectedClient || JSON.parse(localStorage.getItem("selectedClient"));
      let tempClient = {
        ...client,
        form1: encrypt(client?.selectedClient?.form1),
        form2: encrypt(client?.selectedClient?.form2),
        form3: encrypt(client?.selectedClient?.form3),
      };

      if (action?.payload?.newMar?.length > 0) {
        action?.payload?.newMar?.forEach((v, i) => {
          // const _decrypt = decrypt(v?.client?.form3);
          const _decrypt = client?.form3;
          const filtered = _decrypt?.find(
            (x) => Number(x?.id) === Number(v?.medicationId),
          );
          // Always push the record — even if the medication is not found in
          // localStorage form3 (stale cache). Dropping records here caused
          // upcoming virtual entries to be missing for medications added after
          // the client was last selected. The virtual entry generator in MarTab
          // handles form3:null gracefully by falling back to the MAR doc fields.
          _mar.push({
            ...v,
            client: tempClient,
            form3: filtered ?? (v?.medication ? v.medication : null),
          });
        });
      }
      if (action?.payload?.allData?.length > 0) {
        action?.payload?.allData?.forEach((v, i) => {
          const _slotStr = getActiveSlotForDate(v?.timeSlot, v?.date);
          const _isLate = isLate(
            v?.date,
            _slotStr,
            v?.createdAt,
            v?.medicationType,
          );

          const _decrypt = decrypt(v?.client?.form3);
          const filtered = _decrypt?.find(
            (x) => Number(x?.id) === Number(v?.medicationId),
          );
          // _decrypt?.find((x) => x?.id === v?.medicationId) || _decrypt?.[0];
          if (filtered) {
            _allData.push({
              date: moment(v?.date).format("DD-MM-YYYY") || "-",
              clientName:
                decrypt(v?.client?.name, false) || v?.client?.name || "-",
              medicationName: filtered?.medicationName || "-",
              dosage: v?.dosage || "-",
              ingestionMode: filtered?.ingestionMode || "-",
              isNarcotic: filtered?.isNarcotic || "No",
              medicineRemaining: v?.medicineRemaining || "-",
              frequency: v?.form3?.frequency || "-",
              timeSlot: _slotStr || "-",
              staff: v?.staff ? formatTime(v?.createdAt) : "-" || "-",
              staffName: v?.staff?.name || "-",
              medicationType: (() => {
                const medStatus = getSpecificMedicationStatus(
                  v?.medicationType,
                  v?.date,
                  _slotStr,
                );
                return medStatus === "upcoming"
                  ? !moment(v.date).isBefore(moment().format("MM-DD-YYYY"))
                    ? "Upcoming"
                    : "Missed"
                  : medStatus;
              })(),
              late: _isLate,
            });
          }
        });
      }
      return {
        ...state,
        mar: _mar,
        count: action?.payload?.count,
        loading: false,
        allData: _allData,
      };
    },

    setMedicationLogs(state, action) {
      let _mar = [];
      let _allData = [];
      if (action?.payload?.newMar?.length > 0) {
        action?.payload?.newMar?.forEach((v, i) => {
          const _decrypt = decrypt(v?.client?.form3);
          const filtered = _decrypt?.find((x) => x?.id === v?.medicationId);
          _mar.push({
            ...v,
            form3: filtered ? filtered : null,
          });
        });
      }
      if (action?.payload?.allData?.length > 0) {
        action?.payload?.allData?.forEach((v, i) => {
          const _decrypt = decrypt(v?.client?.form3);
          const filtered = _decrypt?.find((x) => x?.id === v?.medicationId);
          _allData.push({
            date: moment(v?.createdAt).format("DD-MM-YYYY") || "-",
            clientName:
              decrypt(v?.client?.name, false) || v?.client?.name || "-",
            medicationName:
              filtered?.medicationName || v?.medication?.medicationName || "-",
            refillCount: v?.refillCount || "-",
            newMedicineCount: v?.newMedicineCount || "-",
          });
        });
      }
      return {
        ...state,
        medicationLogs: _mar,
        medicationLogCount: action?.payload?.count,
        loading: false,
        medicationLogsAllData: _allData,
      };
    },
    setSelectedClient(state, action) {
      return {
        ...state,
        selectedClient: action.payload,
        selectedClientCopy: action.payload,
      };
    },

    searchClient(state, action) {
      const form3 = Array.isArray(state?.selectedClientCopy?.form3)
        ? state?.selectedClientCopy?.form3
        : [];

      const searchedMedicines = form3?.filter((item) =>
        item?.medicationName
          ?.toLowerCase()
          ?.includes(action.payload?.toLowerCase()),
      );

      return {
        ...state,
        selectedClient: {
          ...state.selectedClient,
          form3: searchedMedicines,
        },
      };
    },

    // setSelectedClient(state, action) {
    //   selectedClientCopy = action?.payload;
    //   return {
    //     ...state,
    //     selectedClient: action?.payload,
    //   };
    // },
    // searchClient(state, action) {
    //   let searchedMedicines = selectedClientCopy?.form3?.filter((item) =>
    //     item?.medicationName?.toLowerCase()?.includes(action?.payload)
    //   );
    //   return {
    //     ...state,
    //     selectedClient: {
    //       ...state.selectedClient,
    //       form3: searchedMedicines,
    //     },
    //   };
    // },
    serchMedicinesByStartDate(state, action) {
      const form3 = Array.isArray(state?.selectedClientCopy?.form3)
        ? state?.selectedClientCopy?.form3
        : [];

      // Use UTC to ensure consistent comparison regardless of local timezone.
      // Search for medications that started exactly on the selected date.
      const filterDate = moment.utc(action?.payload).startOf("day");

      let searchedMedicines = form3?.filter((item) => {
        if (!item.medicationDate) return false;
        const medDate = moment.utc(item.medicationDate).startOf("day");
        return medDate.isSame(filterDate);
      });

      return {
        ...state,
        selectedClient: {
          ...state.selectedClient,
          form3: searchedMedicines,
        },
      };
    },
    setFilterDates(state, action) {
      return {
        ...state,
        filterDates: action?.payload,
      };
    },

    setLogType(state, action) {
      return {
        ...state,
        logType: action?.payload,
      };
    },
    setLogSearch(state, action) {
      return {
        ...state,
        logSearch: action?.payload,
      };
    },
    setNewMarLogs(state, action) {
      // Normalise medicationType on every log entry before storing in Redux.
      // Under the new single-document schema, mar.medicationType is an array of
      // {date, status, timeSlot, comments, staff} objects.  If a MarLog record
      // was accidentally saved with the raw array (old bug), rendering
      // row.medicationType directly in JSX crashes with "Objects are not valid
      // as a React child".  Resolve to a plain string here so the table is safe.
      const normalizeMedType = (mt) => {
        if (!mt) return "-";
        if (typeof mt === "string") return mt;
        if (Array.isArray(mt)) {
          if (mt.length === 0) return "upcoming";
          const last = mt[mt.length - 1];
          return typeof last?.status === "string"
            ? last.status
            : String(last?.status ?? "-");
        }
        if (typeof mt === "object" && mt.status) return mt.status;
        return String(mt);
      };

      // The backend returns { count, newMar, allData } — not a flat array.
      // Normalize medicationType on each entry inside newMar and allData.
      const normalizeArr = (arr) =>
        (arr || []).map((log) => ({
          ...log,
          medicationType: normalizeMedType(log?.medicationType),
        }));

      return {
        ...state,
        newMarLogs: {
          count: action?.payload?.count,
          newMar: normalizeArr(action?.payload?.newMar),
          allData: normalizeArr(action?.payload?.allData),
        },
        loading: false,
      };
    },
    resetSelectedClient(state, action) {
      return {
        ...state,
        selectedClient: state?.selectedClientCopy,
      };
    },

    // Reset State
    reset() {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setLoadingMAR_Type,
  setAddMarLoading,
  setMar,
  setCalendarEntries,
  setSelectedClient,
  searchClient,
  setFilterDates,
  setLogType,
  serchMedicinesByStartDate,
  resetSelectedClient,
  setLogSearch,
  setMedicationLogs,
  setNewMarLogs,
} = marState.actions;
export default marState.reducer;
